import React, { useRef, useState } from "react";
import useDocumentOcr from "../hooks/useDocumentOcr";
import { storage } from "../Firebase";
import {
  getUserFacingRejectionMessage,
  isOcrDocumentAccepted,
  isOcrEnabledFor,
  isEmployeeOcrEnabled,
  isOcrInfrastructureError,
} from "../utils/ocrApi";

function shouldHideOcrMessage(message) {
  if (!message || typeof message !== "string") return true;
  return isOcrInfrastructureError(message) && !/\s/.test(message.trim());
}

const statusBox = {
  base: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "0.8125rem",
    lineHeight: 1.45,
  },
  loading: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    color: "rgba(255, 255, 255, 0.9)",
  },
  success: {
    background: "rgba(40, 199, 111, 0.12)",
    border: "1px solid rgba(40, 199, 111, 0.45)",
    color: "#b8f0d0",
  },
  error: {
    background: "rgba(234, 84, 85, 0.12)",
    border: "1px solid rgba(234, 84, 85, 0.45)",
    color: "#f5c2c7",
  },
};

function StatusMessage({ icon, title, detail, variant }) {
  return (
    <div style={{ ...statusBox.base, ...statusBox[variant] }}>
      <i
        className={`bx ${icon}`}
        style={{ fontSize: "1.25rem", flexShrink: 0, marginTop: "1px" }}
        aria-hidden
      />
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {detail ? (
          <div
            style={{
              marginTop: "4px",
              opacity: 0.9,
              fontSize: "0.75rem",
              whiteSpace: "pre-line",
            }}
          >
            {detail}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OcrVerdictPanel({
  result,
  error,
  loading,
  localMessage,
  verifyingLabel,
  expectedName,
  expectedAccount,
  expectedBank,
  documentType,
}) {
  if (loading) {
    return (
      <StatusMessage
        icon="bx-loader-alt bx-spin"
        title={verifyingLabel}
        variant="loading"
      />
    );
  }

  if (localMessage) {
    if (shouldHideOcrMessage(localMessage)) {
      return null;
    }
    return (
      <StatusMessage
        icon="bx-x-circle"
        title="Dokumen tidak valid"
        detail={localMessage}
        variant="error"
      />
    );
  }

  if (error) {
    return null;
  }

  if (!result) return null;

  const matched = isOcrDocumentAccepted(
    result,
    expectedName,
    expectedAccount,
    documentType,
    expectedBank,
  );

  if (matched) {
    return (
      <StatusMessage
        icon="bx-check-circle"
        title="Dokumen valid"
        variant="success"
      />
    );
  }

  return (
    <StatusMessage
      icon="bx-x-circle"
      title="Dokumen tidak valid"
      detail={getUserFacingRejectionMessage(
        result,
        expectedName,
        expectedAccount,
        documentType,
        expectedBank,
      )}
      variant="error"
    />
  );
}

function FilePreview({
  previewMode,
  previewUrl,
  placeholderSrc,
  title,
  fileUrl,
}) {
  if (previewMode === "pdf") {
    const hasFile = fileUrl || previewUrl;
    if (!hasFile) {
      return (
        <img
          src={placeholderSrc}
          alt={title}
          className="mb-1 mt-3"
          style={{ maxWidth: "190px", width: "100%" }}
        />
      );
    }

    return (
      <div className="mb-1 mt-3" style={{ minHeight: "190px" }}>
        <i
          className="bx bxs-file-pdf"
          style={{ fontSize: "100px", color: "#dc3545" }}
        />
        <p className="mt-2 text-white">
          <small>CV berhasil diupload</small>
        </p>
        {fileUrl ? (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-light"
          >
            <i className="bx bx-show" /> Lihat PDF
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <img
      src={previewUrl || placeholderSrc}
      alt={title}
      className="mb-1 mt-3 w-50"
      style={{ maxHeight: 200, objectFit: "contain" }}
    />
  );
}

/**
 * Upload dokumen dengan verifikasi pipeline API sebelum upload ke Firebase Storage.
 */
export default function DocumentOcrUpload({
  documentType,
  expectedName,
  expectedBank = "",
  expectedAccount = "",
  jobPosition = "",
  expectedNik = "",
  cvEducationQuery = "",
  cvExperienceQuery = "",
  title,
  placeholderSrc,
  previewUrl,
  fileUrl,
  fileLabel,
  uploadStatus,
  inputId,
  inputName,
  required,
  storageFolder,
  validateImageFile,
  validateFile,
  accept = "image/x-png,image/jpeg,image/jpg,image/webp",
  fileTypeHint = "gambar PNG / JPG / WebP",
  previewMode = "image",
  maxSizeMb = 3,
  onPreviewChange,
  onStatusChange,
  onLabelChange,
  onUploadComplete,
  userOcrEnabled = false,
}) {
  const inputRef = useRef(null);
  const processingRef = useRef(false);
  const previewGenerationRef = useRef(0);
  const [localMessage, setLocalMessage] = useState(null);

  const clearInputValue = () => {
    const input = inputRef.current;
    if (input) {
      input.value = "";
    }
  };

  const rejectSelectedFile = (message, status) => {
    previewGenerationRef.current += 1;
    setLocalMessage(message);
    onStatusChange(status);
    onLabelChange(null);
    onPreviewChange(null, null);
    clearInputValue();
  };

  const {
    loading: ocrLoading,
    result: ocrResult,
    error: ocrError,
    verify,
    reset: resetOcr,
    isCvValidation,
  } = useDocumentOcr(documentType, expectedName, {
    cvEducationQuery,
    cvExperienceQuery,
    expectedBank,
    expectedAccount,
    jobPosition,
    expectedNik,
  });

  const verifyingStatus = isCvValidation
    ? "Memproses CV..."
    : "Memverifikasi...";
  const ocrEnabled =
    isEmployeeOcrEnabled(userOcrEnabled) && isOcrEnabledFor(documentType);

  const handleInputClick = (e) => {
    e.currentTarget.value = "";
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    if (!input.files || input.files.length === 0) return;
    if (processingRef.current) return;

    const file = input.files[0];
    if (!file) return;

    const fileValidator = validateFile || validateImageFile;

    processingRef.current = true;
    setLocalMessage(null);
    resetOcr();

    try {
      if (fileValidator && !fileValidator(file)) {
        setLocalMessage(
          `Format file tidak valid. Hanya ${fileTypeHint} yang diperbolehkan.`,
        );
        clearInputValue();
        onStatusChange("Format file tidak valid");
        return;
      }

      if (!file.size) {
        setLocalMessage("File tidak valid atau rusak.");
        clearInputValue();
        onStatusChange("File tidak valid");
        return;
      }

      const fileSizeMb = file.size / 1024 / 1024;
      if (fileSizeMb > maxSizeMb) {
        setLocalMessage(`Ukuran file tidak boleh lebih dari ${maxSizeMb} MB.`);
        clearInputValue();
        onStatusChange("Melebihi ukuran maksimal");
        return;
      }

      onLabelChange(file.name);

      const previewGeneration = previewGenerationRef.current + 1;
      previewGenerationRef.current = previewGeneration;

      if (previewMode === "image") {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (previewGenerationRef.current !== previewGeneration) return;
          onPreviewChange(reader.result, file);
        };
        reader.readAsDataURL(file);
      } else {
        onPreviewChange(null, file);
      }

      let validatedByOcr = false;
      let pipelineResult = null;
      if (ocrEnabled) {
        onStatusChange(verifyingStatus);
        try {
          const ocrOutcome = await verify(file, {
            expectedBank,
            expectedAccount,
            expectedNik,
            jobPosition,
          });
          pipelineResult = ocrOutcome.data || null;
          if (ocrOutcome.skipped) {
            setLocalMessage(null);
            resetOcr();
            validatedByOcr = false;
          } else {
            validatedByOcr = ocrOutcome.ok === true;
            if (!validatedByOcr) {
              const message =
                (ocrOutcome.data &&
                  getUserFacingRejectionMessage(
                    ocrOutcome.data,
                    expectedName,
                    expectedAccount,
                    documentType,
                    expectedBank,
                  )) ||
                ocrOutcome.rejectionMessage ||
                ocrOutcome.error ||
                "Dokumen tidak lolos verifikasi.";
              rejectSelectedFile(
                shouldHideOcrMessage(message)
                  ? "Dokumen tidak lolos verifikasi."
                  : message,
                "Verifikasi gagal",
              );
              return;
            }
          }
        } catch (ocrErr) {
          validatedByOcr = false;
          setLocalMessage(null);
          resetOcr();
        }
      }

      onStatusChange("Uploading...");

      const file_name = Date.now() + "-" + file.name;
      const uploadTask = storage.ref(`${storageFolder}/${file_name}`).put(file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            );
            onStatusChange(`Uploading... ${progress}%`);
          },
          (err) => reject(err),
          () => resolve(),
        );
      });

      const url = await storage
        .ref(storageFolder)
        .child(file_name)
        .getDownloadURL();

      clearInputValue();
      if (validatedByOcr) {
        setLocalMessage(null);
      }
      onStatusChange("Upload selesai");
      onUploadComplete(url, file, {
        validate_by_ocr: validatedByOcr,
        issued_date:
          (pipelineResult &&
            (pipelineResult.issued_date ||
              (pipelineResult.validation &&
                pipelineResult.validation.issued_date) ||
              (pipelineResult.ocr &&
                pipelineResult.ocr.extraction &&
                pipelineResult.ocr.extraction.issued_date))) ||
          null,
        issued_date_display:
          (pipelineResult &&
            (pipelineResult.issued_date_display ||
              (pipelineResult.validation &&
                pipelineResult.validation.issued_date_display))) ||
          null,
      });
    } catch (err) {
      setLocalMessage("Upload gagal. Silakan coba lagi.");
      onStatusChange("Upload gagal");
      clearInputValue();
    } finally {
      processingRef.current = false;
    }
  };

  return (
    <div className="card bg-secondary bg-lighten-1">
      <div className="card-content text-white">
        <div className="card-body pt-10">
          <div className="text-center">
            <FilePreview
              previewMode={previewMode}
              previewUrl={previewUrl}
              placeholderSrc={placeholderSrc}
              title={title}
              fileUrl={fileUrl}
            />
            <h4 className="card-title white">{title}</h4>
          </div>
          <div className="card-text">
            <div className="custom-file">
              <input
                ref={inputRef}
                accept={accept}
                required={required}
                name={inputName}
                onClick={handleInputClick}
                onChange={handleFileChange}
                type="file"
                className="custom-file-input"
                id={inputId}
              />
              <label
                className="custom-file-label"
                htmlFor={inputId}
                data-browse="Browse"
              >
                {fileLabel == null ? <span>Pilih File</span> : fileLabel}
              </label>
            </div>
            <p className="text-danger small mb-1 mt-1">
              Maksimal ukuran file {maxSizeMb} MB, Tipe {fileTypeHint}.
            </p>
            {uploadStatus ? (
              <p className="small mb-1">
                <i>{uploadStatus}</i>
              </p>
            ) : null}
            {ocrEnabled ? (
              <OcrVerdictPanel
                result={ocrResult}
                error={ocrError}
                loading={ocrLoading}
                localMessage={localMessage}
                verifyingLabel={verifyingStatus}
                expectedName={expectedName}
                expectedAccount={expectedAccount}
                expectedBank={expectedBank}
                documentType={documentType}
              />
            ) : localMessage ? (
              <OcrVerdictPanel
                result={null}
                error={null}
                loading={false}
                localMessage={localMessage}
                verifyingLabel={verifyingStatus}
                expectedName={expectedName}
                expectedAccount={expectedAccount}
                expectedBank={expectedBank}
                documentType={documentType}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
